import {Card} from 'platform/components';
import {Box, Grid, HStack, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';

import i18n from '@dms/i18n';

import type {ArticleAbcXyz} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

const ABC_CLASSES = ['A', 'B', 'C'] as const;
const XYZ_CLASSES = ['X', 'Y', 'Z'] as const;

interface StatisticsAbcXyzProps {
  classification: ArticleAbcXyz;
  currency: string;
  handlingUnitLabel: string;
}

interface MetricProps {
  label: string;
  value: string;
}

function Metric(props: MetricProps) {
  return (
    <HStack justify="space-between" align="center" wrap>
      <Text color="secondary">{props.label}</Text>
      <Text>{props.value}</Text>
    </HStack>
  );
}

export function StatisticsAbcXyz(props: StatisticsAbcXyzProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatDate = useDateTimeFormatter();
  const number = (value: number | null, digits: number) =>
    value === null ? EMPTY_PLACEHOLDER : (formatNumber(value, digits) ?? EMPTY_PLACEHOLDER);
  const percent = (value: number | null) =>
    value === null ? EMPTY_PLACEHOLDER : (formatNumber(value, 2) ?? EMPTY_PLACEHOLDER) + ' %';
  const date = (value: string | null) =>
    value === null ? EMPTY_PLACEHOLDER : formatDate('dateShort', parseDate(value));
  const period = (start: string | null, end: string | null) =>
    start === null || end === null ? EMPTY_PLACEHOLDER : date(start) + ' – ' + date(end);

  return (
    <Card title={i18n.t('analytics.label.abcXyzAnalysis')}>
      {props.classification.abcClass === null && props.classification.xyzClass === null ? (
        <Text>{i18n.t('general.labels.noData')}</Text>
      ) : (
        <VStack spacing={4}>
          <VStack spacing={1}>
            <HStack spacing={2} align="center">
              <Box width={4} />
              <Box flex={1}>
                <Grid columns={3} spacing={1}>
                  {XYZ_CLASSES.map((xyzClass) => (
                    <Text key={xyzClass} align="center" color="secondary">
                      {xyzClass}
                    </Text>
                  ))}
                </Grid>
              </Box>
            </HStack>
            {ABC_CLASSES.map((abcClass) => (
              <HStack key={abcClass} spacing={2} align="center">
                <Box width={4}>
                  <Text color="secondary">{abcClass}</Text>
                </Box>
                <Box flex={1}>
                  <Grid columns={3} spacing={1}>
                    {XYZ_CLASSES.map((xyzClass) => {
                      const cell = abcClass + xyzClass;
                      const isActive = props.classification.combinedClass === cell;
                      return (
                        <Box
                          key={cell}
                          padding={2}
                          borderRadius="small"
                          backgroundColor={
                            isActive ? 'palettes.blue.60.100' : 'palettes.neutral.10.100'
                          }
                        >
                          <Text align="center" color={isActive ? 'white' : 'secondary'}>
                            {isActive ? cell : ' '}
                          </Text>
                        </Box>
                      );
                    })}
                  </Grid>
                </Box>
              </HStack>
            ))}
          </VStack>
          <HStack spacing={4} wrap>
            <Metric
              label={i18n.t('entity.warehouse.reporting.abcClass')}
              value={props.classification.abcClass ?? EMPTY_PLACEHOLDER}
            />
            <Metric
              label={i18n.t('entity.warehouse.reporting.xyzClass')}
              value={props.classification.xyzClass ?? EMPTY_PLACEHOLDER}
            />
          </HStack>
          <Grid columns={[1, 2]} spacing={3}>
            <VStack spacing={2}>
              <Metric
                label={i18n.t('entity.warehouse.reporting.netConsumptionValue')}
                value={
                  props.classification.netConsumptionValue === null
                    ? EMPTY_PLACEHOLDER
                    : (formatCurrency(
                        props.classification.netConsumptionValue,
                        props.currency,
                        2
                      ) ?? EMPTY_PLACEHOLDER)
                }
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.shareOfTotal')}
                value={percent(props.classification.sharePercent)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.cumulativeShare')}
                value={percent(props.classification.cumulativeSharePercent)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.abcRank')}
                value={number(props.classification.abcRank, 0)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.evaluationPeriod')}
                value={period(
                  props.classification.abcPeriodStart,
                  props.classification.abcPeriodEnd
                )}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.historyLength')}
                value={number(props.classification.abcHistoryMonthCount, 0)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.snapshotDate')}
                value={date(props.classification.abcSnapshotDate)}
              />
            </VStack>
            <VStack spacing={2}>
              <Metric
                label={i18n.t('entity.warehouse.reporting.meanMonthlyIssuedQuantity')}
                value={
                  props.classification.meanMonthlyQuantity === null
                    ? EMPTY_PLACEHOLDER
                    : number(props.classification.meanMonthlyQuantity, 2) +
                      ' ' +
                      props.handlingUnitLabel
                }
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.coefficientOfVariation')}
                value={number(props.classification.coefficientOfVariation, 2)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.yearRepeatability')}
                value={number(props.classification.seasonalityCorrelation, 2)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.activeMonths')}
                value={
                  props.classification.activeMonthCount === null ||
                  props.classification.xyzHistoryMonthCount === null
                    ? EMPTY_PLACEHOLDER
                    : number(props.classification.activeMonthCount, 0) +
                      ' / ' +
                      number(props.classification.xyzHistoryMonthCount, 0)
                }
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.evaluationPeriod')}
                value={period(
                  props.classification.xyzPeriodStart,
                  props.classification.xyzPeriodEnd
                )}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.snapshotDate')}
                value={date(props.classification.xyzSnapshotDate)}
              />
            </VStack>
          </Grid>
        </VStack>
      )}
    </Card>
  );
}
